/**
 * §30 — cálculo de cambio en efectivo. Lógica PURA en CENTAVOS ENTEROS (sin float:
 * un error de centavo en el corte es indistinguible de un faltante real).
 *
 * Política (decisión Arturo): el CAMBIO se redondea HACIA ARRIBA al múltiplo de
 * $0.50 (a favor del cliente), opt-in por tenant. NO toca el total del ticket; el
 * diferencial (redondeado − exacto) lo absorbe el tenant y se registra por venta.
 */

/** $0.50 en centavos — la unidad de redondeo. */
export const ROUND_STEP_CENTS = 50;

/** Convierte un importe (NUMERIC 2 decimales) a centavos enteros de forma segura. */
export function toCents(amount: number | string): number {
  return Math.round((Number(amount) || 0) * 100);
}
export const centsToAmount = (cents: number): number => cents / 100;

export interface ChangeResult {
  /** Cambio exacto (recibido − a cobrar), en centavos. */
  exactCents: number;
  /** Cambio ENTREGADO al cliente (redondeado si aplica), en centavos. */
  changeCents: number;
  /** Diferencial que absorbe el tenant (changeCents − exactCents), en centavos. */
  diffCents: number;
}

/**
 * Cambio a partir de (recibido, a cobrar) en centavos enteros. Asume
 * `receivedCents >= chargeCents` (la validación de "recibido < total" es aparte).
 * Con `rounding` off → cambio exacto, diferencial 0. Con rounding on → redondea el
 * cambio hacia arriba al múltiplo de 50¢; un cambio ya múltiplo de 50¢ (incl. .00)
 * NO genera diferencial.
 */
export function computeChangeCents(receivedCents: number, chargeCents: number, rounding: boolean): ChangeResult {
  const exact = receivedCents - chargeCents;
  if (!rounding) return { exactCents: exact, changeCents: exact, diffCents: 0 };
  const rounded = Math.ceil(exact / ROUND_STEP_CENTS) * ROUND_STEP_CENTS;
  return { exactCents: exact, changeCents: rounded, diffCents: rounded - exact };
}

/** Conveniencia sobre importes (no centavos) — para la UI. `sufficient=false`
 *  cuando recibido < a cobrar (el caller no debe permitir cerrar). */
export function computeChange(received: number, charge: number, rounding: boolean): {
  exact: number; change: number; diff: number; sufficient: boolean;
} {
  const receivedCents = toCents(received);
  const chargeCents = toCents(charge);
  const r = computeChangeCents(receivedCents, chargeCents, rounding);
  return {
    exact: centsToAmount(r.exactCents),
    change: centsToAmount(r.changeCents),
    diff: centsToAmount(r.diffCents),
    sufficient: receivedCents >= chargeCents,
  };
}

/** Siguiente monto redondo por ENCIMA del total, al múltiplo de `step` (default
 *  $100). Ej.: total $325 → $400. Para el botón rápido dinámico del cobro. */
export function nextRoundAbove(total: number, step = 100): number {
  const cents = toCents(total);
  const stepCents = toCents(step);
  return centsToAmount((Math.floor(cents / stepCents) + 1) * stepCents);
}
